import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, RefreshCw, Sparkles, CheckCircle2, AlertTriangle, Lightbulb } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

const TYPE_LABELS = {
    "ortografía": "Ortografía",
    "gramática": "Gramática",
    "puntuación": "Puntuación",
    "concordancia": "Concordancia",
    "redacción": "Redacción",
    "otro": "Otro",
};

const CUMPLE_LABELS = {
    cumple: { label: "Cumple", cls: "bg-emerald-100 text-emerald-700" },
    parcial: { label: "Parcial", cls: "bg-amber-100 text-amber-700" },
    no_cumple: { label: "No cumple", cls: "bg-red-100 text-red-700" },
};

export default function ReporteAlumno() {
    const { courseId, studentId } = useParams();
    const navigate = useNavigate();
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(true);
    const [regenerating, setRegenerating] = useState(false);

    async function load(force = false) {
        if (force) setRegenerating(true); else setLoading(true);
        try {
            const { data } = await api.get(`/courses/${courseId}/students/${studentId}/report`, { params: force ? { force: true } : {} });
            setReport(data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error generando el reporte");
        } finally {
            setLoading(false);
            setRegenerating(false);
        }
    }

    useEffect(() => { load(false); }, [courseId, studentId]);

    if (loading) return <div className="p-10 text-edu-muted">Generando reporte…</div>;
    if (!report) return <div className="p-10 text-edu-muted">No se pudo generar el reporte.</div>;

    const { stats, narrative } = report;
    const a = stats.assignments;
    const errorEntries = Object.entries(a.error_types || {}).sort((x, y) => y[1] - x[1]);
    const rubricEntries = Object.entries(a.rubric_stats || {});

    return (
        <div className="p-6 md:p-10 fade-in min-h-screen max-w-3xl mx-auto">
            <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-sm text-edu-body hover:text-edu-navy mb-6">
                <ArrowLeft size={16} /> Volver
            </button>

            <div className="edu-card p-6 mb-6 flex items-center justify-between flex-wrap gap-3">
                <div>
                    <p className="text-xs text-edu-muted uppercase tracking-widest font-semibold">{report.course_name}</p>
                    <h1 className="text-2xl font-semibold mt-1">{report.student_name}</h1>
                    <p className="text-xs text-edu-muted mt-1">
                        Reporte de la semana {report.week_key} · generado el {new Date(report.generated_at).toLocaleString("es-PE")}
                    </p>
                </div>
                <button onClick={() => load(true)} disabled={regenerating} className="px-3 py-1.5 text-xs rounded-lg border border-edu hover:bg-edu-soft inline-flex items-center gap-1.5 disabled:opacity-50">
                    <RefreshCw size={12} className={regenerating ? "animate-spin" : ""} /> {regenerating ? "Regenerando…" : "Regenerar"}
                </button>
            </div>

            {(narrative?.fortalezas || narrative?.a_mejorar || narrative?.actividad_sugerida) && (
                <div className="space-y-3 mb-6">
                    {narrative.fortalezas && (
                        <div className="edu-card p-5 border-l-4 border-l-emerald-400">
                            <h2 className="text-sm font-semibold text-emerald-800 mb-1 inline-flex items-center gap-1.5"><CheckCircle2 size={14} /> Fortalezas</h2>
                            <p className="text-sm text-edu-body">{narrative.fortalezas}</p>
                        </div>
                    )}
                    {narrative.a_mejorar && (
                        <div className="edu-card p-5 border-l-4 border-l-amber-400">
                            <h2 className="text-sm font-semibold text-amber-800 mb-1 inline-flex items-center gap-1.5"><AlertTriangle size={14} /> En qué debe mejorar</h2>
                            <p className="text-sm text-edu-body">{narrative.a_mejorar}</p>
                        </div>
                    )}
                    {narrative.actividad_sugerida && (
                        <div className="edu-card p-5 border-l-4 border-l-blue-400">
                            <h2 className="text-sm font-semibold text-blue-800 mb-1 inline-flex items-center gap-1.5"><Lightbulb size={14} /> Actividad sugerida para clase</h2>
                            <p className="text-sm text-edu-body">{narrative.actividad_sugerida}</p>
                        </div>
                    )}
                </div>
            )}

            {/* Tareas */}
            <div className="edu-card p-6 mb-6">
                <h2 className="text-sm font-semibold text-edu-navy mb-3">Tareas</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                    <Stat label="Entregadas" value={`${a.submitted}/${a.total_assignments}`} />
                    <Stat label="Calificadas" value={a.graded} />
                    <Stat label="Nota promedio" value={a.avg_score_percent !== null ? `${a.avg_score_percent}%` : "—"} />
                    <Stat label="A tiempo / tarde" value={`${a.on_time} / ${a.late}`} />
                </div>

                {errorEntries.length > 0 && (
                    <div className="mb-4">
                        <p className="text-xs font-semibold text-edu-muted uppercase tracking-widest mb-2">Errores de gramática más frecuentes</p>
                        <div className="space-y-1.5">
                            {errorEntries.map(([type, count]) => {
                                const coursePercent = stats.course_error_percentages?.[type];
                                return (
                                    <div key={type} className="text-sm bg-edu-soft rounded-lg px-3 py-2 flex items-center justify-between flex-wrap gap-1">
                                        <span>{TYPE_LABELS[type] || type} <span className="text-edu-muted">({count})</span></span>
                                        {coursePercent != null && (
                                            <span className="text-xs text-edu-muted">Forma parte del {coursePercent}% de alumnos del curso con este tipo de error</span>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {rubricEntries.length > 0 && (
                    <div>
                        <p className="text-xs font-semibold text-edu-muted uppercase tracking-widest mb-2">Desempeño según rúbricas</p>
                        <div className="space-y-1.5">
                            {rubricEntries.map(([criterio, counts]) => (
                                <div key={criterio} className="text-sm bg-edu-soft rounded-lg px-3 py-2">
                                    <p className="mb-1">{criterio}</p>
                                    <div className="flex gap-1.5 flex-wrap">
                                        {Object.entries(counts).filter(([, c]) => c > 0).map(([k, c]) => (
                                            <span key={k} className={`text-[10px] px-1.5 py-0.5 rounded-full ${CUMPLE_LABELS[k].cls}`}>{CUMPLE_LABELS[k].label} × {c}</span>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Exámenes */}
            <div className="edu-card p-6 mb-6">
                <h2 className="text-sm font-semibold text-edu-navy mb-3">Exámenes</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <Stat label="Rendidos" value={`${stats.exams.taken}/${stats.exams.total}`} />
                    <Stat label="Promedio" value={stats.exams.avg_score_percent !== null ? `${stats.exams.avg_score_percent}%` : "—"} />
                    <Stat label="Promedio del curso" value={stats.exams.course_avg_score_percent !== null ? `${stats.exams.course_avg_score_percent}%` : "—"} />
                    <Stat label="Aprobado / Intermedio / Desaprobado" value={`${stats.exams.by_grade.Aprobado} / ${stats.exams.by_grade.Intermedio} / ${stats.exams.by_grade.Desaprobado}`} />
                </div>
            </div>

            {/* Lecciones */}
            <div className="edu-card p-6">
                <h2 className="text-sm font-semibold text-edu-navy mb-3 inline-flex items-center gap-1.5"><Sparkles size={14} /> Avance en lecciones</h2>
                <Stat label="Lecciones completadas" value={`${stats.lessons.completed}/${stats.lessons.total}`} />
            </div>

            <p className="text-[11px] text-edu-muted mt-6">
                Este reporte se calcula con datos reales de entregas, exámenes y lecciones. No incluye tiempo de estudio
                (no se registra en la plataforma) ni evaluaciones psicológicas — para eso, consulta con el área de psicología del colegio.
            </p>
        </div>
    );
}

function Stat({ label, value }) {
    return (
        <div className="bg-edu-soft rounded-xl p-3 text-center">
            <p className="text-lg font-semibold">{value}</p>
            <p className="text-[10px] text-edu-muted mt-0.5">{label}</p>
        </div>
    );
}
